import React from 'react';
import DOMPurify from 'dompurify';
import {
  Button, Card, Col, Descriptions, Divider, Row, Space, Typography,
} from 'antd';
import { HomeOutlined, PaperClipOutlined } from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import AttachmentPreview from '../components/AttachmentPreview';
import SelectionWizardPanel from '../components/wizard/SelectionWizardPanel';
import { brandSemantic } from '../config/brand';
import { useI18n } from '../i18n';

const { Text } = Typography;

const SOURCING_STATUS_TONES = {
  Pending: { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
  Sourced: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
  'Quote Created': { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
};

/**
 * Category Details page (inquiry-module-redesign) — opened by clicking an
 * Inquiry_Item's category. Shows the item's header fields exactly as the
 * old Product Details screen did (description, target price, branding,
 * special requirements, attachments) minus the product name itself, with
 * the Products → Services → Logistics Selection Wizard embedded below.
 */
export default function CategoryDetailPage({
  item,
  suppliers = [],
  supplierProducts = [],
  products = [],
  services = [],
  onBack,
  draft,
  onDraftChange,
  onSaveWizardResult,
  onOpenProduct,
  onOpenSupplier,
  onAddProduct,
  onCopyProduct,
  t: tProp,
}) {
  const { t: tHook } = useI18n();
  const t = tProp || tHook;

  if (!item) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button icon={<HomeOutlined />} onClick={onBack}>
          {t('Back')}
        </Button>
      </div>

      <Card style={{ borderRadius: 16 }}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('Category')}>
                <Space size={8}>
                  <strong>{item.categoryName}</strong>
                  <SoftTag tone={SOURCING_STATUS_TONES[item.sourcingStatus] || SOURCING_STATUS_TONES.Pending}>
                    {t(item.sourcingStatus || 'Pending')}
                  </SoftTag>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('Quantity')}>{item.quantity ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('Target Price (DKK)')}>
                {item.targetPrice != null ? Number(item.targetPrice).toLocaleString('da-DK') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Re-order')}>{item.reorder ? t('Yes') : t('No')}</Descriptions.Item>
              <Descriptions.Item label={t('Product Description')}>{item.specifications || '—'}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            {(item.brandingDescription || item.deliveryRequirements) && (
              <Descriptions column={1} bordered size="small">
                {item.brandingDescription && (
                  <Descriptions.Item label={t('Branding')}>
                    <div
                      className="rich-text-editor-content"
                      style={{ fontSize: 13, maxHeight: 60, overflow: 'auto' }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.brandingDescription) }}
                    />
                  </Descriptions.Item>
                )}
                {item.deliveryRequirements && (
                  <Descriptions.Item label={t('Special Requirements')}>
                    <div
                      className="rich-text-editor-content"
                      style={{ fontSize: 13, maxHeight: 60, overflow: 'auto' }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.deliveryRequirements) }}
                    />
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}

            <Divider style={{ margin: '16px 0' }} />

            <Card
              size="small"
              title={(
                <Space>
                  <PaperClipOutlined />
                  <span>{t('Attachments')}</span>
                </Space>
              )}
            >
              {(item.files || []).length > 0 ? (
                <AttachmentPreview files={item.files} />
              ) : (
                <Text type="secondary">{t('No attachments yet')}</Text>
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      <SelectionWizardPanel
        item={item}
        supplierProducts={supplierProducts}
        products={products}
        services={services}
        suppliers={suppliers}
        draft={draft}
        onDraftChange={onDraftChange}
        onSave={onSaveWizardResult}
        onCancel={onBack}
        onOpenProduct={onOpenProduct}
        onOpenSupplier={onOpenSupplier}
        onAddProduct={onAddProduct}
        onCopyProduct={onCopyProduct}
      />
    </div>
  );
}
